import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AppShell } from './components/layout'
import { ProtectedRoute } from './components/auth'
import { ErrorBoundary } from './components/ErrorBoundary'
import { PageLoader } from './components/PageLoader'
import { ToastContainer, OfflineBanner } from './components/ui'
import { UndoToastContainer } from './components/ui/UndoToast'
import { NotificationProvider } from './hooks/useNotifications'
import { PersonalizationProvider } from './components/PersonalizationProvider'
import { useAuthStore } from './stores/authStore'
import { SkipLink } from './components/SkipLink'
import './styles/transitions.css'


// Lazy-loaded pages for code splitting - Phase 16
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })))
const Watchlist = lazy(() => import('./pages/Watchlist').then(m => ({ default: m.Watchlist })))
const Intelligence = lazy(() => import('./pages/Intelligence').then(m => ({ default: m.Intelligence })))
const Portfolio = lazy(() => import('./pages/Portfolio').then(m => ({ default: m.Portfolio })))
const Strategies = lazy(() => import('./pages/Strategies').then(m => ({ default: m.Strategies })))
const ScoreExplorer = lazy(() => import('./pages/ScoreExplorer').then(m => ({ default: m.ScoreExplorer })))
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })))
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })))
const SignUp = lazy(() => import('./pages/SignUp').then(m => ({ default: m.SignUp })))
const ContextBuilder = lazy(() => import('./pages/ContextBuilder').then(m => ({ default: m.ContextBuilder })))
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })))
const TechnicalAnalysis = lazy(() => import('./pages/TechnicalAnalysis').then(m => ({ default: m.TechnicalAnalysis })))
const CompareStocks = lazy(() => import('./pages/CompareStocks').then(m => ({ default: m.CompareStocks })))
const IndustryAnalysis = lazy(() => import('./pages/IndustryAnalysis').then(m => ({ default: m.IndustryAnalysis })))
const StockDetail = lazy(() => import('./pages/StockDetail').then(m => ({ default: m.StockDetail })))
const NewsHub = lazy(() => import('./pages/NewsHub').then(m => ({ default: m.NewsHub })))
const OptionsHub = lazy(() => import('./pages/OptionsHub').then(m => ({ default: m.OptionsHub })))
const Screener = lazy(() => import('./pages/Screener').then(m => ({ default: m.Screener })))
const SocialIntelligence = lazy(() => import('./pages/SocialIntelligence').then(m => ({ default: m.SocialIntelligence })))
const ChartingHub = lazy(() => import('./pages/ChartingHub').then(m => ({ default: m.ChartingHub })))
const Backtester = lazy(() => import('./pages/Backtester').then(m => ({ default: m.Backtester })))
const DashboardBuilder = lazy(() => import('./pages/DashboardBuilder').then(m => ({ default: m.DashboardBuilder })))
const AlertsCenter = lazy(() => import('./pages/AlertsCenter').then(m => ({ default: m.AlertsCenter })))
const Pricing = lazy(() => import('./pages/Pricing').then(m => ({ default: m.Pricing })))
const OptionsAnalysis = lazy(() => import('./pages/OptionsAnalysis').then(m => ({ default: m.OptionsAnalysis })))
const EarningsHub = lazy(() => import('./pages/EarningsHub').then(m => ({ default: m.EarningsHub })))
const MarketEvents = lazy(() => import('./pages/MarketEvents').then(m => ({ default: m.MarketEvents })))
const WatchlistIntelligence = lazy(() => import('./pages/WatchlistIntelligence').then(m => ({ default: m.WatchlistIntelligence })))
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })))
const IntelligenceSearch = lazy(() => import('./pages/IntelligenceSearch').then(m => ({ default: m.IntelligenceSearch })))
const PortfolioOptimizer = lazy(() => import('./pages/PortfolioOptimizer').then(m => ({ default: m.PortfolioOptimizer })))

// Feature 1-30 pages
const AIFirstMode = lazy(() => import('./pages/AIFirstMode').then(m => ({ default: m.AIFirstMode })))
const MarketScanner = lazy(() => import('./pages/MarketScanner').then(m => ({ default: m.MarketScanner })))
const PortfolioWhatIf = lazy(() => import('./pages/PortfolioWhatIf').then(m => ({ default: m.PortfolioWhatIf })))
const CommunityIntelligence = lazy(() => import('./pages/CommunityIntelligence').then(m => ({ default: m.CommunityIntelligence })))
const TaxCalculator = lazy(() => import('./pages/TaxCalculator').then(m => ({ default: m.TaxCalculator })))
const ImpactAnalyzer = lazy(() => import('./pages/ImpactAnalyzer').then(m => ({ default: m.ImpactAnalyzer })))
const Admin = lazy(() => import('./pages/Admin').then(m => ({ default: m.Admin })))
const StrategyStudio = lazy(() => import('./pages/StrategyStudio').then(m => ({ default: m.StrategyStudio })))
const StrategyLeaderboard = lazy(() => import('./pages/StrategyLeaderboard').then(m => ({ default: m.StrategyLeaderboard })))
const StrategyComparison = lazy(() => import('./pages/StrategyComparison').then(m => ({ default: m.StrategyComparison })))
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })))

// New pages - Phase 17+
const IPOTracker = lazy(() => import('./pages/IPOTracker').then(m => ({ default: m.IPOTracker })))

// Hub pages - Navigation Consolidation
const Markets = lazy(() => import('./pages/Markets').then(m => ({ default: m.Markets })))

// Phase 16 - Security & Compliance
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })))
const Terms = lazy(() => import('./pages/legal/Terms').then(m => ({ default: m.Terms })))
const Privacy = lazy(() => import('./pages/legal/Privacy').then(m => ({ default: m.Privacy })))

// Phase 17 - Final QA & Launch
const Changelog = lazy(() => import('./pages/Changelog').then(m => ({ default: m.Changelog })))

// Prompt 14 - Score Methodology (public page)
const ScoreMethodology = lazy(() => import('./pages/ScoreMethodology').then(m => ({ default: m.ScoreMethodology })))

// Prompt 17 - Shareable Links
const SharedContent = lazy(() => import('./pages/SharedContent').then(m => ({ default: m.SharedContent })))

// Page title mapping
const PAGE_TITLES: Record<string, string> = {
  '/': 'Dashboard - CipherEquity',
  '/watchlist': 'Watchlist - CipherEquity',
  '/scores': 'Score Explorer - CipherEquity',
  '/alerts': 'Alerts - CipherEquity',
  '/portfolio': 'Portfolio - CipherEquity',
  '/strategies': 'Strategies - CipherEquity',
  '/strategy-studio': 'Strategy Studio - CipherEquity',
  '/strategy-leaderboard': 'Strategy Leaderboard - CipherEquity',
  '/strategy-compare': 'Strategy Comparison - CipherEquity',
  '/settings': 'Settings - CipherEquity',
  '/login': 'Sign In - CipherEquity',
  '/register': 'Create Account - CipherEquity',
  '/signup': 'Sign Up - CipherEquity',
  '/welcome': 'Welcome - CipherEquity',
  '/compare': 'Compare Stocks - CipherEquity',
  '/news': 'News & Intelligence - CipherEquity',
  '/options': 'Options Intelligence - CipherEquity',
  '/screener': 'Stock Screener - CipherEquity',
  '/social': 'Social & Alt Data - CipherEquity',
  '/charts': 'Advanced Charts - CipherEquity',
  '/backtest': 'Backtester - CipherEquity',
  '/dashboard/builder': 'Dashboard Builder - CipherEquity',
  '/pricing': 'Pricing - CipherEquity',
  '/onboarding': 'Welcome to CipherEquity',
  '/options-analysis': 'Options Analysis - CipherEquity',
  '/earnings': 'Earnings Intelligence - CipherEquity',
  '/market-events': 'Market Events - CipherEquity',
  '/watchlist-intelligence': 'Watchlist Intelligence - CipherEquity',
  '/intelligence-search': 'Intelligence Search - CipherEquity',
  '/portfolio-optimizer': 'Portfolio Optimizer - CipherEquity',
  '/ai-chat': 'AI Analysis Mode - CipherEquity',
  '/scanner': 'Market Scanner - CipherEquity',
  '/portfolio/what-if': 'Portfolio What-If - CipherEquity',
  '/community': 'Community Intelligence - CipherEquity',
  '/tax': 'Tax Calculator - CipherEquity',
  '/impact-analyzer': 'Impact Analyzer - CipherEquity',
  '/admin': 'Admin Dashboard - CipherEquity',
  '/ipo': 'IPO & SPAC Tracker - CipherEquity',
  '/changelog': 'Changelog - CipherEquity',
  '/methodology': 'Cipher Score Methodology - CipherEquity',
  // Markets Hub
  '/markets': 'Markets - CipherEquity',
  '/markets/equities': 'Equities - Markets - CipherEquity',
  '/markets/etfs': 'ETFs - Markets - CipherEquity',
  '/markets/fixed-income': 'Fixed Income - Markets - CipherEquity',
  '/markets/commodities': 'Commodities - Markets - CipherEquity',
  '/markets/crypto': 'Crypto - Markets - CipherEquity',
  '/markets/fx': 'FX - Markets - CipherEquity',
  '/markets/dividends': 'Dividends - Markets - CipherEquity',
  '/markets/internals': 'Market Internals - Markets - CipherEquity',
  // Intelligence Hub
  '/intelligence': 'Intelligence - CipherEquity',
  '/intelligence/brief': 'Daily Brief - Intelligence - CipherEquity',
  '/intelligence/analysis': 'AI Analysis - Intelligence - CipherEquity',
  '/intelligence/news': 'News Impact - Intelligence - CipherEquity',
  '/intelligence/causal': 'Causal Network - Intelligence - CipherEquity',
  '/research': 'Research - CipherEquity',
}

function PageTitleUpdater() {
  const location = useLocation()

  useEffect(() => {
    let title = PAGE_TITLES[location.pathname]
    if (!title) {
      if (location.pathname.startsWith('/technical/')) {
        title = 'Technical Analysis - CipherEquity'
      } else if (location.pathname.startsWith('/stock/')) {
        title = 'Stock Detail - CipherEquity'
      } else if (location.pathname.startsWith('/industry/')) {
        title = 'Industry Analysis - CipherEquity'
      } else if (location.pathname.startsWith('/markets/')) {
        title = 'Markets - CipherEquity'
      } else if (location.pathname.startsWith('/intelligence/')) {
        title = 'Intelligence - CipherEquity'
      } else {
        title = 'CipherEquity'
      }
    }
    document.title = title
  }, [location.pathname])

  return null
}

export default function App() {
  const { initAuth, isInitialized } = useAuthStore()

  useEffect(() => {
    if (!isInitialized) {
      initAuth()
    }
  }, [initAuth, isInitialized])

  return (
    <ErrorBoundary>
      <SkipLink />
      <NotificationProvider>
        <PersonalizationProvider>
        <PageTitleUpdater />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/register" element={<Navigate to="/signup" replace />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/welcome" element={<ContextBuilder />} />
            <Route path="/onboarding" element={<Navigate to="/welcome" replace />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/methodology" element={<ScoreMethodology />} />
            <Route path="/shared/:shareId" element={<SharedContent />} />

            {/* Protected Routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<AppShell />}>
                {/* Dashboard now uses unified AppShell like all other pages */}
                <Route path="/" element={<Dashboard />} />
                <Route path="/watchlist" element={<Watchlist />} />
                <Route path="/screener" element={<Screener />} />
                <Route path="/scores" element={<ScoreExplorer />} />
                <Route path="/alerts" element={<AlertsCenter />} />
                <Route path="/news" element={<NewsHub />} />
                <Route path="/options" element={<OptionsHub />} />
                <Route path="/social" element={<SocialIntelligence />} />
                <Route path="/charts" element={<ChartingHub />} />
                <Route path="/backtest" element={<Backtester />} />
                <Route path="/portfolio" element={<Portfolio />} />
                <Route path="/strategies" element={<Strategies />} />
                <Route path="/strategy-studio" element={<StrategyStudio />} />
                <Route path="/strategy-leaderboard" element={<StrategyLeaderboard />} />
                <Route path="/strategy-compare" element={<StrategyComparison />} />
                <Route path="/settings" element={<Settings />} />

                {/* Markets Hub - Consolidated market views */}
                <Route path="/markets" element={<Markets />} />
                <Route path="/markets/:tab" element={<Markets />} />

                {/* Markets Hub Redirects - Old routes to new hub tabs */}
                <Route path="/macro" element={<Navigate to="/markets/equities" replace />} />
                <Route path="/etf" element={<Navigate to="/markets/etfs" replace />} />
                <Route path="/fixed-income" element={<Navigate to="/markets/fixed-income" replace />} />
                <Route path="/dividends" element={<Navigate to="/markets/dividends" replace />} />
                <Route path="/market-internals" element={<Navigate to="/markets/internals" replace />} />
                <Route path="/etfs" element={<Navigate to="/markets/etfs" replace />} />
                <Route path="/etf-intelligence" element={<Navigate to="/markets/etfs" replace />} />
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                <Route path="/strategy" element={<Navigate to="/strategies" replace />} />

                {/* Intelligence Hub - Consolidated AI intelligence */}
                <Route path="/intelligence" element={<Intelligence />} />
                <Route path="/intelligence/:tab" element={<Intelligence />} />

                {/* Intelligence Hub Redirects - Old routes to new hub tabs */}
                <Route path="/ai" element={<Navigate to="/intelligence/analysis" replace />} />
                <Route path="/news-impact" element={<Navigate to="/intelligence/news" replace />} />
                <Route path="/causal-network" element={<Navigate to="/intelligence/causal" replace />} />
                <Route path="/ai-analysis" element={<Navigate to="/intelligence/analysis" replace />} />
                <Route path="/morning-brief" element={<Navigate to="/intelligence/brief" replace />} />
                <Route path="/ipo-tracker" element={<Navigate to="/ipo" replace />} />
                <Route path="/ipo-spac" element={<Navigate to="/ipo" replace />} />
                {/* Bloomberg Features */}
                <Route path="/technical" element={<TechnicalAnalysis />} />
                <Route path="/technical/:ticker" element={<TechnicalAnalysis />} />
                <Route path="/compare" element={<CompareStocks />} />
                <Route path="/industry" element={<IndustryAnalysis />} />
                <Route path="/industry/:sector" element={<IndustryAnalysis />} />
                {/* Stock Detail / Research - Phase 2 */}
                <Route path="/stock/:ticker" element={<StockDetail />} />
                <Route path="/equities/:ticker" element={<StockDetail />} />
                <Route path="/research" element={<StockDetail />} />
                <Route path="/research/:ticker" element={<StockDetail />} />
                {/* User Profile */}
                <Route path="/profile" element={<Profile />} />
                {/* Phase 11-12 - Dashboard Builder */}
                <Route path="/dashboard/builder" element={<DashboardBuilder />} />
                {/* Phase 21-24 - Advanced Analytics */}
                <Route path="/options-analysis" element={<OptionsAnalysis />} />
                <Route path="/earnings" element={<EarningsHub />} />
                <Route path="/market-events" element={<MarketEvents />} />
                {/* Phase 25 - Watchlist Intelligence */}
                <Route path="/watchlist-intelligence" element={<WatchlistIntelligence />} />
                {/* Phase 28 - Enhanced Settings */}
                <Route path="/settings-full" element={<SettingsPage />} />
                {/* Feature 3 - Intelligence Search */}
                <Route path="/intelligence-search" element={<IntelligenceSearch />} />
                {/* Feature 4 - Portfolio Optimizer */}
                <Route path="/portfolio-optimizer" element={<PortfolioOptimizer />} />
                {/* Feature 1-30 Routes */}
                <Route path="/ai-chat" element={<AIFirstMode />} />
                <Route path="/scanner" element={<MarketScanner />} />
                <Route path="/portfolio/what-if" element={<PortfolioWhatIf />} />
                <Route path="/community" element={<CommunityIntelligence />} />
                <Route path="/tax" element={<TaxCalculator />} />
                {/* Causal Intelligence Engine */}
                <Route path="/impact-analyzer" element={<ImpactAnalyzer />} />
                {/* Admin Dashboard - Phase G */}
                <Route path="/admin" element={<Admin />} />
                {/* New Pages - Phase 17+ */}
                <Route path="/ipo" element={<IPOTracker />} />
              </Route>
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <ToastContainer />
        <UndoToastContainer />
        <OfflineBanner />
        </PersonalizationProvider>
      </NotificationProvider>
    </ErrorBoundary>
  )
}
